/**
 * aiConfig.ts – Zaunplaner AI configuration and adapter
 *
 * Combines three concerns:
 *   1. System prompt assembly (FAQ texts, evaluate description)
 *   2. App context gathering (3D scene + UI state)
 *   3. Zaunplaner-specific evaluate function
 *   4. useAiChat hook that configures btg-ai-agent's useAiAgent
 */
import {
  useAiAgent,
  createMessage,
  resolveRuntimeValues,
  createEvaluate,
  roundValues,
} from "btg-ai-agent";
import type { AiAgentMessage } from "btg-ai-agent";
import { getCurrent3dSceneState, EvaluateFunctions, getCurrentUIState } from "./assistantImpl";
export { getCurrentUIState } from "./assistantImpl";
import * as acorn from "acorn";
import { generate } from "astring";
// @ts-ignore
import faqText from "!!raw-loader!./ai_faq_defaults/FAQ.md";
// @ts-ignore
import zaunplanerFAQText from "!!raw-loader!./ai_faq_defaults/Zaunplaner-FAQ.md";
// @ts-ignore
import uiText from "!!raw-loader!./ai_faq_defaults/Zaunplaner-UI.md";
// @ts-ignore
import glossarText from "!!raw-loader!./ai_faq_defaults/Zaunplaner-Glossar.md";
// @ts-ignore
import instructionsText from "!!raw-loader!./ai_faq_defaults/Instructions.md";
// @ts-ignore
import evaluateText from "!!raw-loader!./ai_faq_defaults/Evaluate.md";
// @ts-ignore
import impressumText from "!!raw-loader!./ai_faq_defaults/Impressum.md";
// @ts-ignore
import evaluateApiTypesText from "!!raw-loader!./assistantApi.ts";

// ── Code Formatter ────────────────────────────────────────────────────────────

/**
 * Formats AI-generated JavaScript using acorn (parse) + astring (generate).
 * Falls back to the original code if parsing fails (e.g. incomplete snippets).
 */
export function formatCode(code: string): string {
  try {
    const ast = acorn.parse(code, { ecmaVersion: "latest", sourceType: "script" });
    return generate(ast as any);
  } catch {
    return code;
  }
}

// ── evaluate: App-specific configuration ──────────────────────────────────────

export const evaluate = createEvaluate({
  api: EvaluateFunctions,
  onBeforeExecute: (jsCode) => {
    window["___log"]?.(jsCode);
  },
  onAfterExecute: () => {
    // Trigger immediate 3D rebuild so changes are visible right away
    APP.arrangement.update3d();
    APP.dirty();
    APP.undoHandler.save();
  },
  transformResult: (result) => roundValues(result, 4),
});

// ── App-specific helpers for system prompt ────────────────────────────────────

const getEvaluateDescription = Commons.cache(() => {
  const aiFAQ = getAiFaqText("Evaluate", evaluateText);
  const evaluateApiTypesTextFinal = resolveRuntimeValues(evaluateApiTypesText);
  return aiFAQ + "\n" + evaluateApiTypesTextFinal;
});

/**
 * Look up a key in the AI_FAQ runtime table (managed via backend).
 * Falls back to the static file import if the key doesn't exist or has no text.
 */
function getAiFaqText(key: string, fallback: string): string {
  const rows: any[] = (APP as any).metadata?.AI_FAQ;
  if (rows) {
    const row = rows.find((r: any) => r.Key === key);
    if (row?.Text) return row.Text;
  }
  return fallback;
}

/** Build the full system prompt parts at call-time so runtime AI_FAQ overrides take effect. */
function getSystemPromptParts(): string[] {
  return [
    getAiFaqText("Instructions", instructionsText),
    getAiFaqText("FAQ", faqText),
    getAiFaqText("Impressum", impressumText),
    getAiFaqText("Zaunplaner-FAQ", zaunplanerFAQText),
    getAiFaqText("Zaunplaner-Glossar", glossarText),
    getAiFaqText("Zaunplaner-UI", uiText),
  ];
}

/** Gather the full app context (3D scene + UI state) as a string for the AI. */
function getAppContext(): string {
  const sceneState = JSON.stringify(getCurrent3dSceneState());
  const uiState = JSON.stringify(getCurrentUIState());
  return "<scene3d>\n" + sceneState + "\n</scene3d>\n" + "<ui>\n" + uiState + "\n</ui>";
}

// ── Hook ──────────────────────────────────────────────────────────────────────

export function useAiChat(modelName: string = "gemini-3-flash-preview") {
  const providerName = APP.settings?.ai?.provider || "google";
  const defaultModel = APP.settings?.ai?.model;
  const defaultVisionModel = APP.settings?.ai?.visionModel;
  // If the user manually changed the model, use their choice for vision too
  const visionModel = modelName !== defaultModel ? modelName : defaultVisionModel;

  return useAiAgent({
    provider: providerName,
    model: modelName,
    visionModel,
    proxyUrl: "/aiproxy",
    appTitle: "Traumgarten Zaunplaner",
    systemPromptParts: getSystemPromptParts(),
    evaluateToolDescription: getEvaluateDescription(),
    evaluate,
    getAppContext,
    maxSteps: 20,
    verboseToolResults: true,
    msgToolCallsDone:
      "Ich habe die von Ihnen gewünschte Aktion ausgeführt. Was darf ich als Nächstes tun?",
    msgEmptyResponse: "Entschuldigung, darauf habe ich keine Antwort.",
    msgCancelled: "Abgebrochen.",
    msgConnectionErrorPrefix: "Verbindungsfehler: ",
  });
}
